import React, { useState, useEffect } from 'react';
import { User, Save, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import AvatarSelector, { AvatarDisplay } from '../components/AvatarSelector';
import { profileAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const ProfileSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    avatar: 'avatar_cat',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await profileAPI.get();
      setProfile({
        name: data.name || '',
        email: data.email || '',
        avatar: data.avatar || 'avatar_cat',
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      // Fallback to auth context user
      if (user) {
        setProfile({
          name: user.name || '',
          email: user.email || '',
          avatar: 'avatar_cat',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (saving) return;
    
    try {
      setSaving(true);
      await profileAPI.update({
        name: profile.name,
        avatar: profile.avatar,
      });
      toast.success('Profile updated successfully!');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="profile-settings-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center">
          <User className="w-7 h-7 text-blue-500" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold gradient-text">Profile Settings</h1>
          <p className="text-slate-600 dark:text-slate-400">Customize your profile and avatar</p>
        </div>
      </div>

      {/* Profile Card */}
      <div className="gradient-card rounded-2xl p-6 border border-white/20 dark:border-white/10">
        {/* Current Avatar Display */}
        <div className="flex items-center gap-4 mb-8 pb-6 border-b border-slate-200 dark:border-slate-700">
          <AvatarDisplay avatarId={profile.avatar} size="xl" />
          <div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">{profile.name}</h2>
            <p className="text-slate-500 dark:text-slate-400">{profile.email}</p>
          </div>
        </div>

        {/* Form */}
        <div className="space-y-6">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Display Name</Label>
            <Input
              id="name"
              data-testid="profile-name-input"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
              placeholder="Your name"
            />
          </div>

          {/* Avatar Selection */}
          <div className="space-y-3">
            <Label>Choose Your Avatar</Label>
            <AvatarSelector
              selected={profile.avatar}
              onSelect={(avatarId) => setProfile({ ...profile, avatar: avatarId })}
              size="md"
            />
          </div>

          {/* Save Button */}
          <div className="pt-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-profile-button"
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white shadow-lg"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;
